import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Home, DollarSign, TrendingUp, TrendingDown, MapPin, Calendar,
  Loader2, Sparkles, ArrowRight, Building, Bed, Bath, Ruler,
  CheckCircle, AlertCircle, Lightbulb, BarChart3, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';

const API = process.env.REACT_APP_BACKEND_URL;

const CITIES = [
  { value: 'Toronto', province: 'Ontario' },
  { value: 'Vancouver', province: 'British Columbia' },
  { value: 'Calgary', province: 'Alberta' },
  { value: 'Ottawa', province: 'Ontario' },
  { value: 'Montreal', province: 'Quebec' },
  { value: 'Edmonton', province: 'Alberta' },
  { value: 'Winnipeg', province: 'Manitoba' },
  { value: 'Halifax', province: 'Nova Scotia' },
];

const PropertyEstimator = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    province_state: '',
    property_type: 'house',
    bedrooms: '3',
    bathrooms: '2',
    square_footage: '',
    year_built: '',
    lot_size: '',
    condition: 'good',
    recent_renovations: [],
    nearby_amenities: []
  });

  const handleCityChange = (city) => {
    const cityData = CITIES.find(c => c.value === city);
    setFormData({
      ...formData,
      city,
      province_state: cityData?.province || ''
    });
  };

  const toggleRenovation = (reno) => {
    setFormData(prev => ({
      ...prev,
      recent_renovations: prev.recent_renovations.includes(reno)
        ? prev.recent_renovations.filter(r => r !== reno)
        : [...prev.recent_renovations, reno]
    }));
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      nearby_amenities: prev.nearby_amenities.includes(amenity)
        ? prev.nearby_amenities.filter(a => a !== amenity)
        : [...prev.nearby_amenities, amenity]
    }));
  };

  const getEstimate = async () => {
    if (!formData.city || !formData.square_footage) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API}/api/ai/property-estimate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          bedrooms: parseInt(formData.bedrooms),
          bathrooms: parseInt(formData.bathrooms),
          square_footage: parseInt(formData.square_footage),
          year_built: formData.year_built ? parseInt(formData.year_built) : null
        })
      });

      if (!response.ok) throw new Error('Failed to get estimate');
      
      const data = await response.json();
      setEstimate(data.estimate);
      setStep(3);
    } catch (error) {
      toast.error('Failed to get property estimate');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEstimate(null);
    setStep(1);
    setFormData({
      address: '', city: '', province_state: '', property_type: 'house',
      bedrooms: '3', bathrooms: '2', square_footage: '', year_built: '',
      lot_size: '', condition: 'good', recent_renovations: [], nearby_amenities: []
    });
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="property-estimator-page">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-full mb-4">
          <Sparkles className="w-4 h-4" />
          AI-Powered
        </div>
        <h1 className="text-3xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
          Property Value Estimator
        </h1>
        <p className="text-neutral-500 mt-2 max-w-lg mx-auto">
          Get an instant AI-powered estimate of your property's value based on market data and property characteristics
        </p>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-xl border border-neutral-200 p-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-neutral-600">Step {step} of 3</span>
          <span className="text-neutral-400">{Math.round((step / 3) * 100)}% complete</span>
        </div>
        <Progress value={(step / 3) * 100} className="h-2" />
      </div>

      {/* Step 1: Basic Info */}
      {step === 1 && (
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8 space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-1">Property Details</h2>
            <p className="text-neutral-500 text-sm">Tell us about your property</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Street Address
              </Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="123 Main Street"
                className="mt-2"
                data-testid="input-address"
              />
            </div>

            <div>
              <Label>City *</Label>
              <Select value={formData.city} onValueChange={handleCityChange}>
                <SelectTrigger className="mt-2" data-testid="select-city">
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  {CITIES.map((city) => (
                    <SelectItem key={city.value} value={city.value}>
                      {city.value}, {city.province}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Building className="w-4 h-4" />
                Property Type
              </Label>
              <Select value={formData.property_type} onValueChange={(v) => setFormData({...formData, property_type: v})}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="house">Single Family House</SelectItem>
                  <SelectItem value="condo">Condo</SelectItem>
                  <SelectItem value="townhouse">Townhouse</SelectItem>
                  <SelectItem value="duplex">Duplex</SelectItem>
                  <SelectItem value="semi-detached">Semi-Detached</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Bed className="w-4 h-4" />
                Bedrooms
              </Label>
              <Select value={formData.bedrooms} onValueChange={(v) => setFormData({...formData, bedrooms: v})}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Bath className="w-4 h-4" />
                Bathrooms
              </Label>
              <Select value={formData.bathrooms} onValueChange={(v) => setFormData({...formData, bathrooms: v})}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['1', '1.5', '2', '2.5', '3', '3.5', '4'].map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Ruler className="w-4 h-4" />
                Square Footage *
              </Label>
              <Input
                type="number"
                value={formData.square_footage}
                onChange={(e) => setFormData({...formData, square_footage: e.target.value})}
                placeholder="1500"
                className="mt-2"
                data-testid="input-sqft"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Year Built
              </Label>
              <Input
                type="number"
                value={formData.year_built}
                onChange={(e) => setFormData({...formData, year_built: e.target.value})}
                placeholder="1990"
                className="mt-2"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button onClick={() => setStep(2)} className="bg-[#FF3B30] hover:bg-[#DC2626]" data-testid="next-btn">
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Additional Info */}
      {step === 2 && (
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8 space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-1">Property Condition & Features</h2>
            <p className="text-neutral-500 text-sm">Help us refine your estimate</p>
          </div>

          <div>
            <Label className="mb-3 block">Property Condition</Label>
            <div className="grid grid-cols-4 gap-2">
              {['excellent', 'good', 'fair', 'poor'].map((condition) => (
                <button
                  key={condition}
                  onClick={() => setFormData({...formData, condition})}
                  className={`p-3 rounded-lg border text-sm font-medium capitalize transition-colors ${
                    formData.condition === condition
                      ? 'border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  {condition}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-3 block">Recent Renovations (Select all that apply)</Label>
            <div className="flex flex-wrap gap-2">
              {['Kitchen', 'Bathrooms', 'Flooring', 'Roof', 'Windows', 'HVAC', 'Basement', 'Landscaping'].map((reno) => (
                <button
                  key={reno}
                  onClick={() => toggleRenovation(reno)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    formData.recent_renovations.includes(reno)
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {formData.recent_renovations.includes(reno) && <CheckCircle className="w-4 h-4 inline mr-1" />}
                  {reno}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-3 block">Nearby Amenities</Label>
            <div className="flex flex-wrap gap-2">
              {['Schools', 'Transit', 'Parks', 'Shopping', 'Restaurants', 'Highway'].map((amenity) => (
                <button
                  key={amenity}
                  onClick={() => toggleAmenity(amenity)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    formData.nearby_amenities.includes(amenity)
                      ? 'bg-blue-100 text-blue-700 border border-blue-300'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {formData.nearby_amenities.includes(amenity) && <CheckCircle className="w-4 h-4 inline mr-1" />}
                  {amenity}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            <Button 
              onClick={getEstimate} 
              disabled={loading}
              className="bg-[#FF3B30] hover:bg-[#DC2626]"
              data-testid="get-estimate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Get Estimate
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && estimate && (
        <div className="space-y-6">
          {/* Main Estimate Card */}
          <div className="bg-gradient-to-br from-[#FF3B30] to-[#DC2626] rounded-2xl p-6 sm:p-8 text-white">
            <div className="flex items-center gap-2 mb-4">
              <Home className="w-6 h-6" />
              <h2 className="text-lg font-semibold">Estimated Property Value</h2>
            </div>
            
            <div className="text-center py-6">
              <p className="text-white/70 text-sm uppercase tracking-wide mb-2">Market Value Range</p>
              <div className="flex items-center justify-center gap-4">
                <span className="text-2xl text-white/70">{formatCurrency(estimate.estimated_value_low)}</span>
                <span className="text-5xl font-bold">{formatCurrency(estimate.estimated_value_mid)}</span>
                <span className="text-2xl text-white/70">{formatCurrency(estimate.estimated_value_high)}</span>
              </div>
              <div className="mt-4 flex items-center justify-center gap-4">
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                  {formatCurrency(estimate.price_per_sqft)}/sq ft
                </span>
                <span className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${
                  estimate.market_trend === 'appreciating' ? 'bg-green-500/30' :
                  estimate.market_trend === 'declining' ? 'bg-red-500/30' : 'bg-white/20'
                }`}>
                  {estimate.market_trend === 'appreciating' ? <TrendingUp className="w-4 h-4" /> :
                   estimate.market_trend === 'declining' ? <TrendingDown className="w-4 h-4" /> : null}
                  {estimate.market_trend}
                </span>
              </div>
            </div>

            <div className="text-center text-white/80 text-sm">
              Confidence Level: <span className="font-semibold capitalize">{estimate.confidence_level}</span>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-neutral-700">{estimate.summary}</p>
            </div>
          </div>

          {/* Key Factors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-neutral-200 p-6">
              <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Value Drivers
              </h3>
              <ul className="space-y-2">
                {estimate.key_value_factors?.map((factor, i) => (
                  <li key={i} className="flex items-center gap-2 text-neutral-600">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {factor}
                  </li>
                ))}
              </ul>
            </div>

            {estimate.value_detractors?.length > 0 && (
              <div className="bg-white rounded-xl border border-neutral-200 p-6">
                <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  Considerations
                </h3>
                <ul className="space-y-2">
                  {estimate.value_detractors?.map((factor, i) => (
                    <li key={i} className="flex items-center gap-2 text-neutral-600">
                      <AlertCircle className="w-4 h-4 text-orange-500" />
                      {factor}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Renovation Recommendations */}
          {estimate.renovation_recommendations?.length > 0 && (
            <div className="bg-white rounded-xl border border-neutral-200 p-6">
              <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-600" />
                Renovation Recommendations
              </h3>
              <div className="space-y-3">
                {estimate.renovation_recommendations.map((rec, i) => (
                  <div key={rec.item || `rec-${i}`} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <span className="font-medium text-neutral-800">{rec.item}</span>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-neutral-500">Cost: {formatCurrency(rec.estimated_cost)}</span>
                      <span className="text-green-600 font-medium">+{formatCurrency(rec.value_add)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Market Note */}
          <div className="bg-neutral-50 rounded-xl p-4 text-sm text-neutral-600">
            <p className="font-medium mb-1">Market Context</p>
            <p>{estimate.comparable_properties_note}</p>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={resetForm}>
              New Estimate
            </Button>
            <Button 
              onClick={() => toast.info('Save feature coming soon!')}
              className="bg-[#FF3B30] hover:bg-[#DC2626]"
            >
              Save Estimate
            </Button>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <p className="text-xs text-neutral-400 text-center">
        This estimate is for informational purposes only and should not be used as a substitute for a professional appraisal. 
        Actual property values may vary based on market conditions and other factors.
      </p>
    </div>
  );
};

export default PropertyEstimator;
